import React, {
  ChangeEvent,
  ClipboardEvent,
  KeyboardEvent,
  useState,
} from 'react';
import { Modal } from '../Modal';
import { useTranslation } from 'react-i18next';
import {
  normalizeAddMultipleTracksQuantityInput,
  parseAddMultipleTracksQuantity,
} from '@/utils/trackQuantityUtils';
import { isNonEmptyDigitString } from '@/utils/helper';

interface AddMultipleTracksModalProps {
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
  onSubmit: (addMultipleTrackInput: string) => void;
}

const AddMultipleTracksModal = ({
  onSubmit,
  showModal,
  setShowModal,
}: AddMultipleTracksModalProps) => {
  const [addMultipleTrackInput, setAddMultipleTrackInput] = useState('');
  const isValidTrackQuantity =
    parseAddMultipleTracksQuantity(addMultipleTrackInput) !== null;

  const handleSubmit = () => {
    if (!isValidTrackQuantity) {
      return;
    }

    handleClose();
    onSubmit(addMultipleTrackInput);
  };
  const handleClose = () => {
    setShowModal(false);
    setAddMultipleTrackInput('');
  };

  const handleAddMultipleTrackInput = (evt: ChangeEvent<HTMLInputElement>) => {
    const value = normalizeAddMultipleTracksQuantityInput(evt.target.value);
    if (value === null) {
      evt.target.value = addMultipleTrackInput;
      return;
    }

    setAddMultipleTrackInput(value);
  };

  const handleAddMultipleTrackKeyDown = (
    evt: KeyboardEvent<HTMLInputElement>
  ) => {
    if (['.', ',', 'e', 'E', '+', '-'].includes(evt.key)) {
      evt.preventDefault();
    }
  };

  const handleAddMultipleTrackPaste = (
    evt: ClipboardEvent<HTMLInputElement>
  ) => {
    if (!isNonEmptyDigitString(evt.clipboardData.getData('text'))) {
      evt.preventDefault();
    }
  };

  const { t } = useTranslation();
  return (
    <Modal
      titleText={t('product.productMultipleTracks.addMultipleTracks')}
      submitButtonText={t('common.button.addTracks')}
      submitButtonDisabled={!isValidTrackQuantity}
      show={showModal}
      onSubmit={handleSubmit}
      onClose={handleClose}
      backdrop="static"
    >
      <label>{t('product.productMultipleTracks.addMultipleTracksLabel')}</label>
      <input
        className="form-control"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        aria-invalid={addMultipleTrackInput !== '' && !isValidTrackQuantity}
        value={addMultipleTrackInput}
        placeholder={t(
          'product.productMultipleTracks.addMultipleTracksPlaceholder'
        )}
        onKeyDown={handleAddMultipleTrackKeyDown}
        onPaste={handleAddMultipleTrackPaste}
        onChange={handleAddMultipleTrackInput}
      />
      <p>{t('product.productMultipleTracks.addMultipleTracksInfo')}</p>
    </Modal>
  );
};

export default AddMultipleTracksModal;
